import { useState, useEffect, useRef, useMemo } from 'react'
import ReactQuill from 'react-quill-new'
import 'react-quill-new/dist/quill.snow.css'
import {
  Mail,
  Edit3,
  Save,
  X,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
  Search,
  Tag,
  Code,
  Type,
} from 'lucide-react'
import { adminApi } from '../../lib/api/admin'
import { usePermissionStore } from '../../stores/permissionStore'
import { friendlyApiError } from '../../lib/utils/apiError'

interface EmailTemplate {
  id: number
  template_key: string
  name: string
  subject: string
  body_html: string
  available_variables: string[]
  category: string
  recipient_type: string
  is_enabled: boolean
  updated_by: number | null
  created_at: string
  updated_at: string
}

const CATEGORY_LABELS: Record<string, string> = {
  auth: 'Authentication',
  session: 'Session',
  evaluation: 'Evaluation',
  onboarding: 'Onboarding',
  booking: 'Booking',
  payment: 'Payment',
  recording: 'Recording',
}

const CATEGORY_COLORS: Record<string, string> = {
  auth: '#6366f1',
  session: '#0ea5e9',
  evaluation: '#f59e0b',
  onboarding: '#10b981',
  booking: '#8b5cf6',
  payment: '#16a34a',
  recording: '#ec4899',
}

export default function AdminEmailTemplates() {
  const { can } = usePermissionStore()
  const canEdit = can('email_templates', 'edit')

  const [templates, setTemplates] = useState<EmailTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editForm, setEditForm] = useState({ name: '', subject: '', body_html: '' })
  const [saving, setSaving] = useState(false)
  const [previewHtml, setPreviewHtml] = useState<string | null>(null)
  const [previewSubject, setPreviewSubject] = useState<string>('')
  const [editorMode, setEditorMode] = useState<'visual' | 'html'>('visual')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const quillRef = useRef<ReactQuill | null>(null)

  useEffect(() => {
    fetchTemplates()
  }, [])

  const fetchTemplates = async () => {
    try {
      setFetchError(null)
      const data = await adminApi.getEmailTemplates()
      setTemplates(Array.isArray(data) ? data : [])
    } catch (err) {
      setFetchError(friendlyApiError(err, 'Failed to load email templates'))
    } finally {
      setLoading(false)
    }
  }

  const handleToggleEnabled = async (tpl: EmailTemplate) => {
    if (!canEdit) return
    try {
      const updated = await adminApi.updateEmailTemplate(tpl.id, { is_enabled: !tpl.is_enabled })
      setTemplates(templates.map((t) => (t.id === tpl.id ? updated : t)))
    } catch (err) {
      alert(friendlyApiError(err, 'Failed to update template'))
    }
  }

  const handleStartEdit = (tpl: EmailTemplate) => {
    setEditingId(tpl.id)
    setEditForm({ name: tpl.name, subject: tpl.subject, body_html: tpl.body_html })
    setExpandedId(tpl.id)
    setEditorMode('visual') // always default to visual mode for non-technical admins
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditForm({ name: '', subject: '', body_html: '' })
  }

  const handleSaveEdit = async (tpl: EmailTemplate) => {
    setSaving(true)
    try {
      const updated = await adminApi.updateEmailTemplate(tpl.id, editForm)
      setTemplates(templates.map((t) => (t.id === tpl.id ? updated : t)))
      setEditingId(null)
    } catch (err) {
      alert(friendlyApiError(err, 'Failed to save template'))
    } finally {
      setSaving(false)
    }
  }

  // Pretty-print HTML with simple indentation (so HTML view is readable)
  const formatHTML = (html: string): string => {
    if (!html || !html.trim()) return ''
    const tokens = html
      .replace(/>\s+</g, '><')
      .split(/(<[^>]+>)/)
      .filter((t) => t && t.trim())
    const selfClosing = /^<(br|hr|img|input|meta|link|source|area|base|col|embed|param|track|wbr)\b/i
    let indent = 0
    const lines: string[] = []
    for (const token of tokens) {
      if (token.startsWith('</')) {
        indent = Math.max(0, indent - 1)
        lines.push('  '.repeat(indent) + token)
      } else if (token.startsWith('<') && !selfClosing.test(token) && !token.endsWith('/>') && !token.startsWith('<!')) {
        lines.push('  '.repeat(indent) + token)
        indent++
      } else if (token.startsWith('<')) {
        lines.push('  '.repeat(indent) + token)
      } else {
        lines.push('  '.repeat(indent) + token.trim())
      }
    }
    return lines.join('\n')
  }

  const SAMPLE_VALUES: Record<string, string> = {
    full_name: 'John Doe',
    dashboard_url: '#',
    reset_link: '#',
    to_email: 'john@example.com',
    interviewer_alias: 'Sarah Miller',
    scheduled_at: 'Thursday, April 16, 2026 at 2:00 PM IST (8:30 AM UTC)',
    session_id: '1042',
    session_url: '#',
    booking_id: '305',
    zoom_host_url: '#',
    zoom_join_url: '#',
    zoom_password: '123456',
    feedback_url: '#',
    display_id: '1042',
    eval_link: '#',
    eval_url: '#',
    pay_tier: 'Gold',
    setup_link: '#',
    invitee_name: 'John Doe',
    candidate_name: 'John Doe',
    interviewer_name: 'Sarah Miller',
    position: 'Senior Software Engineer',
    password_block: '<li><strong>Meeting Password:</strong> 123456</li>',
    zoom_block: '',
    host_block: '',
    // application_declined shape — see SAMPLE_OVERRIDES for the templates whose
    // backend emits a different block under this same variable name.
    reason_block: '<p><strong>Feedback:</strong> We are looking for more experience in this area.</p>',
    reason: 'We are looking for more experience in this area.',
    client_name: 'John Doe',
    amount: '49.99',
    schedule_url: '#',
    view_url: '#',
    old_when: 'Tuesday, March 24, 2026 at 2:00 PM IST (8:30 AM UTC)',
    new_when: 'Thursday, March 26, 2026 at 4:00 PM IST (10:30 AM UTC)',
    new_date: 'Thursday, March 26',
    duration_minutes: '60',
    link_note: '<p>Your meeting link has been refreshed for the new time, so please open your interview page to join &mdash; any link from an earlier email will no longer work.</p>',
    // Admin-marked no-show + change-request / extension-request templates.
    session_when: 'Thursday, March 26, 2026 at 4:00 PM IST (10:30 AM UTC)',
    session_date: 'Thursday, March 26',
    sessions_url: '#',
    request_type: 'reschedule',
    request_type_title: 'Reschedule',
    requested_at: 'Tuesday, March 24, 2026 at 9:15 AM UTC',
    admin_queue_url: '#',
    current_window_days: '45',
    effective_window_days: '52',
    max_window_days: '60',
    no_show_party: 'client',
    effective_until: '2026-05-15',
    admin_note: 'Approved for the conference period.',
    // Both callout blocks below are what _reason_callout() / _note_callout()
    // actually emit; see SAMPLE_OVERRIDES for the per-template wording.
    admin_note_block: '',
    note_block: '',
    // `lead` is composed by the Celery task from no_show_party, so it has no
    // single sample — every template that uses it overrides it below.
    lead: '',
  }

  // Some variable names mean different things in different templates:
  // `reason_block` is a "Feedback:" paragraph in application_declined but the
  // gray "Reason:" callout that _reason_callout() emits in the admin-reschedule
  // pair. Per-template overrides keep every preview faithful to what the backend
  // actually sends instead of trading one wrong preview for another.
  // The exact markup _reason_callout() / _note_callout() emit, so a preview shows
  // the gray callout the recipient really gets rather than a bare paragraph.
  const calloutBlock = (label: string, text: string): string =>
    `<div style="margin:14px 0;padding:10px 14px;background:#f1f5f9;border-left:3px solid #94a3b8;border-radius:4px;font-size:12px;color:#475569;"><strong>${label}:</strong> ${text}</div>`

  const RESCHEDULE_REASON_CALLOUT = calloutBlock('Reason', 'Emergency schedule change')
  const NO_REASON_CALLOUT = calloutBlock('Reason', 'No reason given.')
  const ADMIN_NOTE_CALLOUT = calloutBlock(
    'Note from the team',
    'Thanks for the detail — we have granted this for the conference period.',
  )
  const NO_ADMIN_NOTE_CALLOUT = calloutBlock('Note from the team', "The team didn't add a note.")

  const SAMPLE_OVERRIDES: Record<string, Record<string, string>> = {
    booking_rescheduled_admin_client: { reason_block: RESCHEDULE_REASON_CALLOUT },
    booking_rescheduled_admin_interviewer: {
      reason_block: RESCHEDULE_REASON_CALLOUT,
      // The global link_note sample is the CLIENT copy; the interviewer email
      // sends the host-link wording.
      link_note:
        '<p>A new Zoom meeting was created for the new time &mdash; your host link has changed. Start the session from your schedule page; don\'t use the host link from the earlier email.</p>',
    },
    // ── Change requests ───────────────────────────────────────────────────────
    change_request_submitted_admin: { reason_block: NO_REASON_CALLOUT },
    change_request_rejected_interviewer: { admin_note_block: NO_ADMIN_NOTE_CALLOUT },
    // ── Availability extension ────────────────────────────────────────────────
    extension_requested_admin: { reason_block: NO_REASON_CALLOUT },
    availability_extension_approved: {
      admin_note_block: ADMIN_NOTE_CALLOUT,
      // Retained for any admin whose stored body still references {{note_block}}.
      note_block: ADMIN_NOTE_CALLOUT,
    },
    availability_extension_rejected: {
      admin_note_block: NO_ADMIN_NOTE_CALLOUT,
      note_block: NO_ADMIN_NOTE_CALLOUT,
    },
    availability_extension_revoked: {
      note_block: '<p><strong>Reason:</strong> The conference period has ended.</p>',
    },
  }

  const samplesFor = (templateKey?: string): Record<string, string> =>
    templateKey && SAMPLE_OVERRIDES[templateKey]
      ? { ...SAMPLE_VALUES, ...SAMPLE_OVERRIDES[templateKey] }
      : SAMPLE_VALUES

  // Build the full rendered email HTML (with header/footer wrapper and sample
  // variable values). Used for both the inline preview and the Preview modal.
  const wrapEmailHtml = (body: string, templateKey?: string): string => {
    let html = body
    for (const [key, val] of Object.entries(samplesFor(templateKey))) {
      html = html.split(`{{${key}}}`).join(val)
    }
    return `<!DOCTYPE html>
<html><head>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.5; color: #333; max-width: 600px; margin: 0 auto; padding: 20px; font-size: 13px; }
  .header { background: #2563eb; color: white; padding: 16px 20px; text-align: center; }
  .header h1 { margin: 0; font-size: 18px; }
  .content { background: #f8fafc; padding: 24px 20px; border: 1px solid #e2e8f0; border-top: none; border-radius: 0 0 8px 8px; font-size: 13px; }
  .content p { margin: 8px 0; font-size: 13px; }
  .button { display: inline-block; background: #2563eb; color: white !important; padding: 10px 24px; text-decoration: none; border-radius: 6px; font-weight: 600; margin: 16px 0; font-size: 13px; cursor: default; }
  .button:hover { background: #2563eb !important; }
  .schedule-box { background: white; border: 1px solid #bfdbfe; border-left: 4px solid #2563eb; border-radius: 6px; padding: 12px 16px; margin: 16px 0; }
  .schedule-box h3 { margin: 0 0 8px; color: #1e40af; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; }
  .schedule-row { display: flex; gap: 8px; margin: 4px 0; font-size: 13px; }
  .schedule-label { font-weight: 600; min-width: 50px; color: #374151; }
  .cancelled-box { background: white; border: 1px solid #fecaca; border-left: 4px solid #dc2626; border-radius: 6px; padding: 12px 16px; margin: 16px 0; }
  .cancelled-box h3 { margin: 0 0 8px; color: #991b1b; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; }
  .note { font-size: 11px; color: #6b7280; margin-top: 12px; padding: 10px 12px; background: #f1f5f9; border-radius: 6px; }
  a { pointer-events: none; cursor: default; }
  .logo-bar { background: #ffffff; padding: 14px 20px 10px; text-align: center; border-radius: 8px 8px 0 0; border: 1px solid #e2e8f0; border-bottom: none; }
  .logo-bar img { max-width: 60px; height: auto; }
  .header { border-radius: 0; }
  .footer { margin-top: 20px; font-size: 11px; color: #94a3b8; text-align: center; }
</style>
</head><body>
  <div class="logo-bar"><img src="https://www.interviewdressrehearsal.com/InterviewDressRehearsal_LOGO.png" alt="Interview Dress Rehearsal" width="60"></div>
  <div class="header"><h1>Interview Dress Rehearsal Platform</h1></div>
  <div class="content">${html}</div>
  <div class="footer"><p>This is an automated message from Interview Dress Rehearsal Platform. Please do not reply.</p></div>
</body></html>`
  }

  const renderSubjectWithSamples = (subject: string, templateKey?: string): string => {
    let out = subject
    for (const [key, val] of Object.entries(samplesFor(templateKey))) {
      out = out.split(`{{${key}}}`).join(val)
    }
    return out
  }

  const handlePreview = (tpl: EmailTemplate) => {
    const body = editingId === tpl.id ? editForm.body_html : tpl.body_html
    const subject = editingId === tpl.id ? editForm.subject : tpl.subject
    setPreviewHtml(wrapEmailHtml(body, tpl.template_key))
    setPreviewSubject(renderSubjectWithSamples(subject, tpl.template_key))
  }

  const insertVariable = (variable: string) => {
    if (!variable) return
    const tag = `{{${variable}}}`
    if (editorMode === 'html' && textareaRef.current) {
      const ta = textareaRef.current
      const start = ta.selectionStart
      const end = ta.selectionEnd
      const newValue = editForm.body_html.substring(0, start) + tag + editForm.body_html.substring(end)
      setEditForm({ ...editForm, body_html: newValue })
      setTimeout(() => {
        ta.focus()
        ta.setSelectionRange(start + tag.length, start + tag.length)
      }, 0)
    } else if (editorMode === 'visual' && quillRef.current) {
      const editor = quillRef.current.getEditor()
      const range = editor.getSelection(true) ?? { index: editor.getLength(), length: 0 }
      editor.insertText(range.index, tag, 'user')
      editor.setSelection(range.index + tag.length, 0)
    } else {
      // Fallback: append to end if editor ref isn't ready yet
      setEditForm({ ...editForm, body_html: editForm.body_html + tag })
    }
  }

  const switchEditorMode = (mode: 'visual' | 'html') => {
    if (mode === editorMode) return
    // When switching to HTML view, pretty-print the current body for readability
    if (mode === 'html') {
      setEditForm({ ...editForm, body_html: formatHTML(editForm.body_html) })
    }
    setEditorMode(mode)
  }

  // Quill toolbar + modules configuration (memoized to avoid recreation)
  const quillModules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['link'],
        ['blockquote', 'code-block'],
        ['clean'],
      ],
    }),
    [],
  )

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'bullet', 'align',
    'link', 'blockquote', 'code-block',
  ]

  const categories = ['all', ...Array.from(new Set(templates.map((t) => t.category))).sort(), 'admin']

  // "Admin" is not a real `category` value — it's a cross-cutting view over
  // every template an admin action triggers: the two admin-recipient queue
  // notifications (change_request_submitted_admin, extension_requested_admin)
  // plus the admin-initiated client/interviewer pairs (reschedule, cancel,
  // no-show-marked) that live under the Booking category. All eight share
  // "admin" in their template_key, so that substring is the filter.
  const isAdminTemplate = (tpl: EmailTemplate) => tpl.template_key.includes('admin')

  const filtered = templates.filter((tpl) => {
    if (categoryFilter === 'admin') {
      if (!isAdminTemplate(tpl)) return false
    } else if (categoryFilter !== 'all' && tpl.category !== categoryFilter) {
      return false
    }
    if (search) {
      const q = search.toLowerCase()
      return (
        tpl.name.toLowerCase().includes(q) ||
        tpl.template_key.toLowerCase().includes(q) ||
        tpl.subject.toLowerCase().includes(q)
      )
    }
    return true
  })

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <div className="spinner" />
        <span style={{ marginLeft: 12, color: 'var(--text-muted)' }}>Loading templates...</span>
      </div>
    )
  }

  if (fetchError) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Mail size={48} style={{ opacity: 0.3, marginBottom: 12, color: '#ef4444' }} />
        <h3 style={{ fontWeight: 600, fontSize: 16, color: '#ef4444' }}>Failed to Load Templates</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 16 }}>{fetchError}</p>
        <button className="btn btn-primary" onClick={fetchTemplates} style={{ padding: '8px 20px', fontSize: 13 }}>
          Retry
        </button>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 className="page-title" style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>
            <Mail size={24} style={{ marginRight: 10, verticalAlign: 'middle', color: 'var(--accent-gold)' }} />
            Email Templates
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 4 }}>
            Manage email templates sent to clients, interviewers and admins. {templates.length} templates total.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 360 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            className="form-input"
            placeholder="Search templates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ paddingLeft: 36, width: '100%' }}
          />
        </div>
        <div className="tabs" style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {categories.map((cat) => (
            <button
              key={cat}
              className={`tab ${categoryFilter === cat ? 'active' : ''}`}
              onClick={() => setCategoryFilter(cat)}
              style={{ padding: '6px 14px', fontSize: 13, borderRadius: 20, textTransform: 'capitalize' }}
            >
              {cat === 'all' ? 'All' : cat === 'admin' ? 'Admin' : CATEGORY_LABELS[cat] || cat}
            </button>
          ))}
        </div>
      </div>

      {/* Template List */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
          <Mail size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
          <h3 style={{ fontWeight: 600, fontSize: 16 }}>No templates found</h3>
          <p style={{ fontSize: 14 }}>Try adjusting your search or filter.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map((tpl) => {
            const isExpanded = expandedId === tpl.id
            const isEditing = editingId === tpl.id
            const catColor = CATEGORY_COLORS[tpl.category] || '#6b7280'

            return (
              <div
                key={tpl.id}
                className="card"
                style={{
                  padding: 0,
                  borderLeft: `4px solid ${catColor}`,
                  opacity: tpl.is_enabled ? 1 : 0.6,
                }}
              >
                {/* Row header */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '16px 20px',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    if (!isEditing) setExpandedId(isExpanded ? null : tpl.id)
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                      <span style={{ fontWeight: 600, fontSize: 15 }}>{tpl.name}</span>
                      <span
                        style={{
                          fontSize: 11,
                          padding: '2px 8px',
                          borderRadius: 10,
                          background: catColor + '18',
                          color: catColor,
                          fontWeight: 600,
                          textTransform: 'capitalize',
                        }}
                      >
                        {CATEGORY_LABELS[tpl.category] || tpl.category}
                      </span>
                      <span
                        style={{
                          fontSize: 11,
                          padding: '2px 8px',
                          borderRadius: 10,
                          background: '#f1f5f9',
                          color: '#64748b',
                          textTransform: 'capitalize',
                        }}
                      >
                        {tpl.recipient_type}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      Subject: {tpl.subject}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    {canEdit && (
                      <button
                        className={`btn btn-sm ${tpl.is_enabled ? 'btn-success' : 'btn-warning'}`}
                        style={{ padding: '4px 12px', fontSize: 12, borderRadius: 14 }}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleToggleEnabled(tpl)
                        }}
                        title={tpl.is_enabled ? 'Click to disable' : 'Click to enable'}
                      >
                        {tpl.is_enabled ? (
                          <><Eye size={13} style={{ marginRight: 4 }} />Enabled</>
                        ) : (
                          <><EyeOff size={13} style={{ marginRight: 4 }} />Disabled</>
                        )}
                      </button>
                    )}
                    {!isEditing && canEdit && (
                      <button
                        className="btn btn-sm btn-secondary"
                        style={{ padding: '4px 12px', fontSize: 12 }}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleStartEdit(tpl)
                        }}
                      >
                        <Edit3 size={13} style={{ marginRight: 4 }} /> Edit
                      </button>
                    )}
                    {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </div>
                </div>

                {/* Expanded / Edit Panel */}
                {isExpanded && (
                  <div style={{ borderTop: '1px solid var(--border)', padding: '16px 20px' }}>
                    {/* Variables chips */}
                    {tpl.available_variables.length > 0 && (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>
                          <Tag size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                          Available Variables:
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {tpl.available_variables.map((v) => (
                            <span
                              key={v}
                              onClick={() => isEditing && insertVariable(v)}
                              style={{
                                fontSize: 12,
                                padding: '3px 10px',
                                borderRadius: 12,
                                background: '#eff6ff',
                                color: '#2563eb',
                                fontFamily: 'monospace',
                                cursor: isEditing ? 'pointer' : 'default',
                                border: '1px solid #bfdbfe',
                              }}
                              title={isEditing ? `Click to insert {{${v}}}` : v}
                            >
                              {`{{${v}}}`}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {isEditing ? (
                      <>
                        {/* Edit: Name */}
                        <div className="form-group" style={{ marginBottom: 12 }}>
                          <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, display: 'block' }}>Template Name</label>
                          <input
                            className="form-input"
                            value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            style={{ width: '100%' }}
                          />
                        </div>

                        {/* Edit: Subject */}
                        <div className="form-group" style={{ marginBottom: 12 }}>
                          <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, display: 'block' }}>Subject Line</label>
                          <input
                            className="form-input"
                            value={editForm.subject}
                            onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })}
                            style={{ width: '100%' }}
                          />
                        </div>

                        {/* Edit: Body — with Visual / HTML toggle */}
                        <div className="form-group" style={{ marginBottom: 12 }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                            <label style={{ fontSize: 13, fontWeight: 600, display: 'block' }}>Email Body</label>
                            <div style={{ display: 'flex', gap: 4, background: '#f1f5f9', borderRadius: 6, padding: 2 }}>
                              <button
                                type="button"
                                onClick={() => switchEditorMode('visual')}
                                style={{
                                  padding: '4px 10px',
                                  fontSize: 12,
                                  border: 'none',
                                  background: editorMode === 'visual' ? 'white' : 'transparent',
                                  color: editorMode === 'visual' ? '#2563eb' : '#64748b',
                                  boxShadow: editorMode === 'visual' ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                                  borderRadius: 4,
                                  cursor: 'pointer',
                                  fontWeight: 600,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4,
                                }}
                              >
                                <Type size={12} /> Visual
                              </button>
                              <button
                                type="button"
                                onClick={() => switchEditorMode('html')}
                                style={{
                                  padding: '4px 10px',
                                  fontSize: 12,
                                  border: 'none',
                                  background: editorMode === 'html' ? 'white' : 'transparent',
                                  color: editorMode === 'html' ? '#2563eb' : '#64748b',
                                  boxShadow: editorMode === 'html' ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                                  borderRadius: 4,
                                  cursor: 'pointer',
                                  fontWeight: 600,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4,
                                }}
                              >
                                <Code size={12} /> HTML
                              </button>
                            </div>
                          </div>

                          {editorMode === 'visual' ? (
                            <div className="email-template-editor" style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                              <ReactQuill
                                ref={quillRef}
                                theme="snow"
                                value={editForm.body_html}
                                onChange={(content) => setEditForm({ ...editForm, body_html: content })}
                                modules={quillModules}
                                formats={quillFormats}
                                style={{ minHeight: 300 }}
                              />
                            </div>
                          ) : (
                            <textarea
                              ref={textareaRef}
                              className="form-input"
                              value={editForm.body_html}
                              onChange={(e) => setEditForm({ ...editForm, body_html: e.target.value })}
                              rows={16}
                              style={{
                                width: '100%',
                                fontFamily: 'monospace',
                                fontSize: 13,
                                lineHeight: 1.5,
                                resize: 'vertical',
                              }}
                            />
                          )}

                          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                            {editorMode === 'visual'
                              ? 'Use the toolbar to format text. Click a variable chip above or use the Insert variable dropdown to add placeholders. Click Preview to see the final rendered email.'
                              : 'Advanced: edit raw HTML. The code is auto-formatted for readability. Use this if you need to add custom CSS or complex markup.'}
                          </p>
                        </div>

                        {/* Edit actions */}
                        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                          <button
                            className="btn btn-primary"
                            onClick={() => handleSaveEdit(tpl)}
                            disabled={saving}
                            style={{ padding: '8px 20px', fontSize: 13 }}
                          >
                            <Save size={14} style={{ marginRight: 4 }} />
                            {saving ? 'Saving...' : 'Save Changes'}
                          </button>
                          <button
                            className="btn btn-secondary"
                            onClick={() => handlePreview(tpl)}
                            style={{ padding: '8px 20px', fontSize: 13 }}
                          >
                            <Eye size={14} style={{ marginRight: 4 }} /> Preview
                          </button>
                          <button
                            className="btn btn-outline"
                            onClick={handleCancelEdit}
                            style={{ padding: '8px 20px', fontSize: 13 }}
                          >
                            <X size={14} style={{ marginRight: 4 }} /> Cancel
                          </button>

                          {/* Insert Variable dropdown — makes it easy to add a variable without scrolling up to the chips */}
                          {tpl.available_variables.length > 0 && (
                            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
                              <Tag size={13} style={{ color: 'var(--text-muted)' }} />
                              <select
                                className="form-input"
                                value=""
                                onChange={(e) => {
                                  insertVariable(e.target.value)
                                  e.target.value = ''
                                }}
                                style={{
                                  padding: '7px 10px',
                                  fontSize: 13,
                                  fontFamily: 'monospace',
                                  minWidth: 200,
                                  cursor: 'pointer',
                                }}
                              >
                                <option value="">Insert variable…</option>
                                {tpl.available_variables.map((v) => (
                                  <option key={v} value={v}>{`{{${v}}}`}</option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        {/* View: rendered email preview (iframe) — same as Preview modal output */}
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>
                          <Eye size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                          Rendered Preview:
                        </div>
                        <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', background: 'white' }}>
                          <iframe
                            title={`Preview ${tpl.template_key}`}
                            srcDoc={wrapEmailHtml(tpl.body_html, tpl.template_key)}
                            sandbox="allow-same-origin"
                            style={{ width: '100%', height: 380, border: 'none', display: 'block' }}
                          />
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                          <button
                            className="btn btn-secondary"
                            onClick={() => handlePreview(tpl)}
                            style={{ padding: '8px 20px', fontSize: 13 }}
                          >
                            <Eye size={14} style={{ marginRight: 4 }} /> Preview in Full Size
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Preview Modal */}
      {previewHtml && (
        <div
          className="modal-backdrop"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setPreviewHtml(null)}
        >
          <div
            className="card"
            style={{
              width: '90%',
              maxWidth: 700,
              maxHeight: '85vh',
              overflow: 'auto',
              padding: 0,
              borderRadius: 12,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '14px 20px',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Email Preview</h3>
              <button
                className="btn btn-outline"
                onClick={() => setPreviewHtml(null)}
                style={{ padding: '4px 10px', fontSize: 12 }}
              >
                <X size={14} /> Close
              </button>
            </div>
            {previewSubject && (
              <div style={{ padding: '10px 20px', borderBottom: '1px solid var(--border)', background: '#f8fafc' }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>Subject: </span>
                <span style={{ fontSize: 14, fontWeight: 500 }}>{previewSubject}</span>
              </div>
            )}
            <div style={{ padding: 20 }}>
              <iframe
                title="Email Preview"
                srcDoc={previewHtml}
                sandbox="allow-same-origin"
                style={{
                  width: '100%',
                  height: 500,
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  background: 'white',
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
